import { useRef, useMemo } from 'react';
import { buildNodeMaps, normalizeAncestorsOnlySelection, removeSelectedValue } from '../utils/tree-select-checkbox-selection.utils.js';
import { getTreeSelectSelectionFlags, getCanonicalTreeSelectValue } from '../utils/tree-select-selection-model.utils.js';
import { createChangeEvent } from '../utils/tree-select.utils.js';
import { useTreeSelectValuePresentation } from './UseTreeSelectValuePresentation.js';

function useTreeSelectSelectionModel(props) {
  const {
    checkboxSelectionBehavior = 'native',
    onChange,
    onNodeSelect,
    onNodeUnselect,
    options,
    selectionMode,
    selectedValueLayout,
    value,
    valueTemplate: inputValueTemplate
  } = props;
  const interactionRef = useRef(null);
  const selectionFlags = useMemo(() => getTreeSelectSelectionFlags({
    checkboxSelectionBehavior,
    display: props.display,
    inputValueTemplate,
    options,
    selectedValueLayout,
    selectionMode
  }), [checkboxSelectionBehavior, inputValueTemplate, options, props.display, selectedValueLayout, selectionMode]);
  const nodeMaps = useMemo(() => selectionFlags.shouldBuildNodeMaps ? buildNodeMaps(options) : undefined, [options, selectionFlags.shouldBuildNodeMaps]);
  const normalizedValue = useMemo(() => {
    return getCanonicalTreeSelectValue(value, nodeMaps, selectionFlags.isAncestorsOnlyCheckboxMode);
  }, [nodeMaps, selectionFlags.isAncestorsOnlyCheckboxMode, value]);
  function emitNormalizedChange(event) {
    if (!selectionFlags.isAncestorsOnlyCheckboxMode || !nodeMaps) {
      onChange === null || onChange === void 0 ? void 0 : onChange(event);
      return;
    }
    const nextValue = normalizeAncestorsOnlySelection({
      nextValue: event.value,
      previousValue: normalizedValue,
      nodeMaps,
      interaction: interactionRef.current
    });
    interactionRef.current = null;
    onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign(Object.assign({}, event), {
      value: nextValue,
      target: Object.assign(Object.assign({}, event.target), {
        value: nextValue
      })
    }));
  }
  function handleNodeSelect(event) {
    if (selectionFlags.isAncestorsOnlyCheckboxMode) {
      interactionRef.current = {
        action: 'select',
        key: String(event.node.key)
      };
    }
    onNodeSelect === null || onNodeSelect === void 0 ? void 0 : onNodeSelect(event);
  }
  function handleNodeUnselect(event) {
    if (selectionFlags.isAncestorsOnlyCheckboxMode) {
      interactionRef.current = {
        action: 'unselect',
        key: String(event.node.key)
      };
    }
    onNodeUnselect === null || onNodeUnselect === void 0 ? void 0 : onNodeUnselect(event);
  }
  function handleDismissSelectedValue(key, event) {
    if (!nodeMaps || !selectionMode) {
      return;
    }
    const nextValue = removeSelectedValue({
      key,
      nodeMaps,
      selectionMode,
      value: normalizedValue,
      preserveAncestorsOnlySelection: selectionFlags.isAncestorsOnlyCheckboxMode
    });
    emitNormalizedChange(createChangeEvent(event, props, nextValue));
  }
  const {
    display,
    selectedNodes,
    shouldRenderSelectedPills,
    valueTemplate
  } = useTreeSelectValuePresentation({
    display: props.display,
    inputValueTemplate,
    nodeMaps,
    placeholder: props.placeholder,
    selectionFlags,
    selectionMode,
    value: normalizedValue
  });
  return {
    display,
    selectedNodes,
    shouldRenderSelectedPills,
    value: normalizedValue,
    valueTemplate,
    handleChange: emitNormalizedChange,
    handleDismissSelectedValue,
    handleNodeSelect,
    handleNodeUnselect
  };
}

export { useTreeSelectSelectionModel };
